import React, {useCallback, useEffect, useState} from "react";
import { Alert, AlertProps, Box, Button } from "@mui/material";
import {
    DataGrid,
    GridColDef,
    GridRowId, GridRowModel,
    GridRowModes,
    GridRowModesModel,
    GridRowsProp,
    GridToolbarContainer, GridValidRowModel,
} from "@mui/x-data-grid";
import {getActiveAccount, getToken, isUser, useAuthentication, useLogin} from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { Edit, Save, Cancel } from "@mui/icons-material";
import Snackbar from "@mui/material/Snackbar";
import { getDomainList, updateDomain, DomainOption, DomainListResponse } from "../../api";
import { ADMIN_ROLES, NO_ACCESS_ROLE } from "../../constants";
import { FsnUser } from "@fusion/components/common";


interface EditToolbarProps {
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
    setRowModesModel: (newModel: (oldModel: GridRowModesModel) => GridRowModesModel) => void;
    editMode: boolean;
    setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
    toggleEditMode: (saveChanges: boolean) => void;
    refreshTable: () => Promise<void>;
    // handleOpen: () => void;
    selectedDomainRow: GridRowId[];
}


function EditToolbar(props: EditToolbarProps) {
    const { isAdmin } = useAuthentication();
    const { editMode, toggleEditMode } = props;


    const handleEditSaveClick = () => {
        if (editMode) {
            console.debug("Disabled editing mode");
            toggleEditMode(true);
        }
        else {
            console.debug("Enabled editing mode");
            toggleEditMode(false);
        }
    };


    const handleCancelClick = () => {
        // Cancel edit mode
        console.debug("Canceled domain update and disabled editing mode");
        toggleEditMode(false);
    }


    return (
        isAdmin() && (
            <GridToolbarContainer>
                {/* for future capability to dynamically add domains
                <Button color="primary" startIcon={<Add />} onClick={() => setNewRowDialogOpen(true)}>
                    Add Domain
                </Button>*/}
                <Button color="primary" startIcon={editMode ? <Save /> : <Edit />} onClick={handleEditSaveClick}>
                    {editMode ? 'Save' : 'Edit Domains'}
                </Button>
                {editMode && <Button color="primary" startIcon={<Cancel />} onClick={handleCancelClick}>Cancel</Button>}
            </GridToolbarContainer>
        )
    )
}


const DomainTable = () => {
    const [rows, setRows] = useState<GridRowsProp>([]);
    const [snackbar, setSnackbar] = useState<Pick<AlertProps, "children" | "severity"> | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedDomainRow, setSelectedDomainRow] = React.useState<GridRowId[]>([]);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const client = useLogin ? useMsal().instance : undefined;
    const { isAdmin } = useAuthentication();
    let loadingCoordinator = 0;


    const refreshTable = async () => {
        let resp;
        try {
            updateLoadingCoordinator(true);
            const token = client ? await getToken(client) : undefined;
            const response = await getDomainList(token);
            resp = response;
            const data = (await response.json()) as DomainListResponse;
            const domains = data.domains.sort(
                (a, b) => parseInt(a.domain_id) - parseInt(b.domain_id)
            )
            setRows(domains.map((row: DomainOption) => ({
                id: row.domain_id,
                domain_id: row.domain_id,
                domain_name: row.domain_name,
                active_status: row.active_status,
                saveChangesMode: false
            })));
            console.debug("current rows status: " + rows);
        }
        catch (error) {
            setSnackbar({ children: "Error fetching domains", severity: "error" });
            console.error(error);
            console.error(resp);
        } finally {
            updateLoadingCoordinator(false);
        }
    };


    // updateLoadingCoordinator is used to track the number of loading operations in progress
    function updateLoadingCoordinator(add: boolean) {
        add ? loadingCoordinator++ : loadingCoordinator--;
        loadingCoordinator == 0 ? setLoading(false) : setLoading(true);
    }


    // Hmm, I wonder what this does? 🤔 (*sarcastic tone*)
    const getUserInfo = () => {
        const user: FsnUser = {
            firstName: "Default-Firstname",
            lastName: "Default-Lastname",
            roles: [NO_ACCESS_ROLE]
        };

        const idClaims = client ? JSON.stringify(getActiveAccount(client)?.idTokenClaims ?? {}) : undefined;
        if (idClaims) {
            const userInfo = JSON.parse(idClaims);
            let userRoles = !isUser(getActiveAccount(client)) ? [NO_ACCESS_ROLE] : userInfo.roles;
            userRoles = userRoles.filter((role: string) => ADMIN_ROLES.includes(role))
            user.firstName = userInfo.givenName ?? "[Unknown first name]";
            user.lastName = userInfo.sn ?? "[Unknown last name]";
            user.roles = userRoles;
        }

        return user;
    }


    // Triggered whenever the edit, save, or cancel button is clicked
    const toggleEditMode = (saveChanges: boolean)  => {
        if (editMode) {
            // 'Save' or 'Cancel' was clicked
            const newRows = rows.map((row) => {
                return { ...row, saveChangesMode: saveChanges };
            });
            setRows(newRows);

            if (!saveChanges) {
                // 'Cancel' was clicked
                refreshTable();
            }

            const newRowModesModel = {...rowModesModel};
            for (const rowIndex in rows) {
                newRowModesModel[rows[rowIndex].id] = {mode: GridRowModes.View};
            }
            setRowModesModel(newRowModesModel);
        }
        else {
            // 'Edit' was clicked
            const newRowModesModel = {...rowModesModel};
            for (const rowIndex in rows) {
                newRowModesModel[rows[rowIndex].id] = {mode: GridRowModes.Edit};
            }
            setRowModesModel(newRowModesModel);
        }
        setEditMode(!editMode);
    };


    // mutateRow is what actually updates the database when a row change is detected
    const mutateRow = async (newRow: GridRowModel): Promise<Response> => {
        let response: Response;
        try {
            if (!isAdmin()) {
                setSnackbar({ children: "Error: Could not update domains - Admin role not found.", severity: "error" })
                throw new Error("Error: Could not update domains - Admin role not found.");
            }
            const token = client ? await getToken(client) : undefined;
            response = await updateDomain(token, newRow.id, newRow as DomainOption);
        } catch (error) {
            throw error;
        }
        return response;
    };


    // processRowUpdate is triggered for each row whenever it switches between 'edit' and 'view' mode
    // this is controlled individually for each row, so it triggers multiple times each time the table is saved
    // (the DataGrid component we've used for our tables technically supports editing individual cells or rows, but not the entire grid)
    const processRowUpdate = useCallback(
        async (newRow: GridRowModel, previousRow: GridRowModel) => {
            // don't save changes if the row hasn't changed or the cancel button was hit
            if (JSON.stringify(newRow) === JSON.stringify(previousRow)
            || !newRow.saveChangesMode) {
                return previousRow;
            }
            try {
                updateLoadingCoordinator(true);
                const updatedRow = { ...newRow, isNew: false };
                const response = await mutateRow(newRow);
                if (response.status !== 200) {
                    const data = await response.json();

                    handleProcessRowUpdateError(new Error(data.error) as Error);
                    return previousRow;
                } else {
                    await refreshTable();
                    setSnackbar({ children: "Domains updated successfully", severity: "success" });
                }
                return updatedRow;
            } catch (error) {
                console.error("error updating row")
                handleProcessRowUpdateError(error as Error);
                return previousRow;
            } finally {
                updateLoadingCoordinator(false);
            }
        },
        [setSnackbar, mutateRow]
    );


    const handleProcessRowUpdateError = useCallback((error: Error) => {
        setSnackbar({ children: "There was an error saving the domain updates.", severity: "error" });
        console.error({ children: error.message, severity: "error" });
    }, [setSnackbar]);


    // fetch data and load the table on first render
    useEffect(() => {
        refreshTable();
    }, [])


    const handleCloseSnackbar = () => setSnackbar(null);


    const columns: GridColDef[] = [
        {
            field: "domain_name",
            headerName: "Domain",
            width: 150,
            editable: true
        },
        {
            field: "active_status",
            headerName: "Status",
            type: "boolean",
            width: 100,
            editable: true
        },
    ];


    return (
        <Box
            sx={{
                height: 800,
                width: "100%",
                "& .actions": {
                    color: "text.secondary"
                },
                "& .textPrimary": {
                    color: "text.primary"
                },
                "& .activate": {
                    color: "green"
                },
                "& .deactivate": {
                    color: "red"
                }
            }}
        >
            <DataGrid
                rows={rows}
                columns={columns}
                editMode="row"
                disableRowSelectionOnClick
                rowModesModel={rowModesModel}
                processRowUpdate={processRowUpdate}
                slots={{
                    toolbar: () => (
                        <EditToolbar
                            setRows={setRows}
                            editMode={editMode}
                            setEditMode={setEditMode}
                            refreshTable={refreshTable}
                            setRowModesModel={setRowModesModel}
                            toggleEditMode={toggleEditMode}
                            selectedDomainRow={selectedDomainRow}
                        />
                    ),
                }}
                onRowSelectionModelChange={(newSelection) => {
                    setSelectedDomainRow(newSelection.length > 0 ? [newSelection[0]] : []);
                }}
                loading={loading}
                getRowHeight={() => 'auto'}
            />
            {!!snackbar && (
                <Snackbar open anchorOrigin={{ vertical: "bottom", horizontal: "center" }} onClose={handleCloseSnackbar} autoHideDuration={6000}>
                    <Alert {...snackbar} onClose={handleCloseSnackbar} />
                </Snackbar>
            )}
        </Box>
    )
}
export default DomainTable;